/**
 * MoneyInput Component
 * Reusable input for Currency with live formatting based on user preferences
 * Task 000b - Amount Input Formatting
 * Task 000h - Custom Amount Keyboard with Math Calculation
 * Task 000i - Currency and Thousand Separator Preferences
 */

import React, { useState, useRef, useEffect } from 'react';
import { AmountKeyboard } from './AmountKeyboard';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export interface MoneyInputProps {
  value: number | null;
  onValueChange: (next: number) => void;
  allowNegative?: boolean;
  id?: string;
  name?: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  useCustomKeyboard?: boolean;
  keyboardTitle?: string;
}

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  onValueChange,
  allowNegative = false,
  id,
  name,
  className = '',
  autoFocus = false,
  disabled = false,
  placeholder,
  style,
  useCustomKeyboard = false,
  keyboardTitle = 'Masukkan Jumlah',
}) => {
  const { formatMoney, parseMoney } = useUserPreferences();
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  
  // Desktop detection (lg: 1024px+)
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Format the value for display
  useEffect(() => {
    if (value === null) {
      setDisplayValue('');
    } else if (value === 0) {
      // Use formatMoney to get the correct symbol and format for 0
      setDisplayValue(formatMoney(0));
    } else {
      setDisplayValue(formatMoney(value));
    }
  }, [value, formatMoney]); // Re-run when value or formatting preferences change

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    // Extract only digits (and minus if allowed)
    const digitsBeforeCursor = input.slice(0, cursorPosition).replace(/[^0-9-]/g, '').length;

    // Parse the input value using context utility
    const numericValue = parseMoney(input);

    // Update parent with numeric value
    onValueChange(numericValue);

    // Calculate new cursor position after formatting
    // We'll restore it in the next tick
    setTimeout(() => {
      if (inputRef.current) {
        const formattedValue = inputRef.current.value;
        let newCursorPos = 0;
        let digitCount = 0;

        for (let i = 0; i < formattedValue.length; i++) {
          if (/[0-9-]/.test(formattedValue[i])) {
            digitCount++;
            if (digitCount >= digitsBeforeCursor) {
              newCursorPos = i + 1;
              break;
            }
          }
        }

        // If we've counted all digits and haven't reached cursor position,
        // place cursor at end
        if (digitCount < digitsBeforeCursor || newCursorPos === 0) {
          newCursorPos = formattedValue.length;
        }

        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numericValue = parseMoney(pastedText);
    onValueChange(numericValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (allowedKeys.includes(e.key)) {
      return;
    }

    // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }

    // Allow minus only at the start if allowNegative
    if (e.key === '-' && allowNegative) {
      return;
    }

    // Allow only digits
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Handle input click/focus when using custom keyboard
  const handleInputClick = () => {
    if (useCustomKeyboard && !disabled) {
      setIsKeyboardOpen(true);
      // On mobile: blur input to prevent native keyboard
      // On desktop: keep focus so user knows where they're typing (keyboard events captured by AmountKeyboard)
      if (!isDesktop) {
        inputRef.current?.blur();
      }
    }
  };

  // Scroll category row into view and add padding when keyboard opens (mobile only)
  useEffect(() => {
    // Skip scroll/padding adjustments on desktop - keyboard is centered in modal
    if (isDesktop) return;
    
    if (isKeyboardOpen && inputRef.current) {
      const keyboardHeight = 450; // Approximate keyboard height + some padding

      // Find parent category row (look for closest element with category data)
      let targetElement: HTMLElement = inputRef.current;
      let parent = inputRef.current.parentElement;

      // Try to find the category row wrapper (has p-2.5 class in SubCategoryRow)
      while (parent && !parent.classList.contains('p-2.5')) {
        parent = parent.parentElement;
        if (parent && (parent.classList.length > 2 || parent.tagName === 'SECTION')) {
          targetElement = parent;
          break;
        }
      }
      if (parent && parent.classList.contains('p-2.5')) {
        targetElement = parent;
      }

      // Add bottom padding FIRST, then scroll
      document.body.style.paddingBottom = `${keyboardHeight}px`;

      // Wait for padding to be applied, then scroll
      setTimeout(() => {
        const rect = targetElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const targetTop = rect.top + window.scrollY;

        // Position so the row is just above the keyboard
        // Keyboard takes up bottom ~420px, so position element at viewport - keyboard - some margin
        const desiredPosition = targetTop - (viewportHeight - keyboardHeight - 20);

        window.scrollTo({
          top: desiredPosition,
          behavior: 'smooth'
        });
      }, 100);
    } else {
      // Remove padding when keyboard closes
      document.body.style.paddingBottom = '0';
    }

    return () => {
      // Cleanup: remove padding on unmount
      document.body.style.paddingBottom = '0';
    };
  }, [isKeyboardOpen, isDesktop]);

  // Handle keyboard confirm
  const handleKeyboardConfirm = (calculatedValue: number) => {
    onValueChange(calculatedValue);
    setIsKeyboardOpen(false);
  };

  // Determine placeholder based on currency if not provided
  const effectivePlaceholder = placeholder || formatMoney(0);

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        inputMode={useCustomKeyboard ? 'none' : 'numeric'}
        id={id}
        name={name}
        value={displayValue}
        onChange={useCustomKeyboard ? undefined : handleChange}
        onPaste={useCustomKeyboard ? undefined : handlePaste}
        onKeyDown={useCustomKeyboard ? undefined : handleKeyDown}
        onClick={handleInputClick}
        onFocus={handleInputClick}
        className={className}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={effectivePlaceholder}
        style={style}
        readOnly={useCustomKeyboard}
      />

      {/* Custom Amount Keyboard */}
      {useCustomKeyboard && (
        <AmountKeyboard
          isOpen={isKeyboardOpen}
          onClose={() => setIsKeyboardOpen(false)}
          onConfirm={handleKeyboardConfirm}
          initialValue={value || 0}
          allowNegative={allowNegative}
          title={keyboardTitle}
        />
      )}
    </>
  );
};
